// The exception states (content.json exceptions, titles from the design generator), shared by both concepts. Each is
// reached the way a household would meet it: a refused or uncertain payment, a pending activation, Contact BOB, an
// unserved address or paused intake, an urgent concern, a photo the service stopped on, a new device, an outage.
import { useState, type ReactNode } from 'react';
import { useStore, type Exc } from './store.tsx';
import { Button, Bracket, Chip, Eyebrow, KV } from './ds.tsx';
import { CaseCard, ReceiptCard, T } from './cards.tsx';
import { W, money, unitsWord } from './words.ts';
import { excTitle, view } from '../../content/content.ts';

const Tone = ({ tone, children }: { tone: 'good' | 'warning' | 'critical' | 'accent' | 'neutral'; children: ReactNode }) => <span className={`hc-chip hc-chip--${tone}`}>{children}</span>;

function Layout({ eyebrow, title, sub, children, actions, note }: { eyebrow: ReactNode; title: string; sub?: ReactNode; children?: ReactNode; actions: ReactNode; note?: string }) {
  return (
    <>
      <header className="rs-intro"><div className="rs-wrap">{eyebrow}</div><h1 className="rs-h1">{title}</h1>{sub && <p className="rs-muted">{sub}</p>}</header>
      {children}
      <div className="rs-actions">{actions}{note && <p className="hc-caption rs-center">{note}</p>}</div>
    </>
  );
}
const Quiet = ({ children }: { children: ReactNode }) => <div className="rs-row-actions">{children}</div>;
const Link = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => <Button kind="quiet" className="rs-auto" onClick={onClick}>{children}</Button>;

export function ExceptionScreen({ k }: { k: Exc }) {
  const s = useStore();
  const h = s.h;
  const member = h?.membership.state !== 'none' && !!h;
  const contact = <Link onClick={() => s.contact()}>{W.contactBob}</Link>;
  const done = <Button kind="primary" onClick={s.closeExc}>{W.done}</Button>;
  const last = h?.payments.at(-1);
  const payEyebrow = <><Eyebrow>{W.paymentTitle}</Eyebrow><Chip>{W.testPrices}</Chip></>;
  const returnLater = <Link onClick={() => s.openRoute(W.returnLater, W.returnNote)}>{W.returnLater}</Link>;

  switch (k) {
    case 'decline': {
      const pick = h?.decline ?? null;
      const setPick = (r: 'delete' | 'keep') => s.act({ type: 'decline_join', retention: r });
      return (
        <Layout eyebrow={<Eyebrow>{view(1, 1).controls[3]}</Eyebrow>} title={excTitle('decline')} sub="Nothing is purchased or charged. No reason is needed, and no one will try to change your mind."
                actions={<><Button kind="primary" onClick={() => { s.closeExc(); s.setView(1); }}>{W.done}</Button><Quiet>{contact}</Quiet></>}>
          <section className="hc-card rs-stack-8">
            <KV rows={[[W.quote, h?.quote ? `${h.quote.ref} ${h.quote.version}` : 'None'], [W.photos, String(s.records?.photos.length ?? 0)], ['Retention', <T>[retention per your choice; rule unresolved]</T>]]} />
            <div className="rs-wrap">
              {(['delete', 'keep'] as const).map((d) => <button key={d} type="button" className="hc-btn hc-btn--quiet rs-auto rs-choice-btn rs-press" aria-pressed={pick === d} onClick={() => setPick(d)}>{d === 'delete' ? 'Delete my information' : 'Keep it for later'}</button>)}
            </div>
          </section>
        </Layout>
      );
    }
    case 'person-requested': case 'person-accepted': case 'person-unavailable': {
      const c = h?.cases.find((x) => x.kind === 'person' && x.state !== 'closed');
      return (
        <Layout eyebrow={<Eyebrow>{W.personLabel}</Eyebrow>} title={excTitle(k)}
                sub={k === 'person-requested' ? 'Requested is not accepted. An owner and update appear here only when a person actually accepts.' : k === 'person-unavailable' ? 'No one is available right now. Your request is kept.' : undefined}
                actions={<>{done}{k === 'person-unavailable' && <Button onClick={() => s.openRoute('Published fallback', '[fallback contact and hours]')}>Use the fallback</Button>}</>}>
          {c && <CaseCard k={c} />}
        </Layout>
      );
    }
    case 'photo-skipped': return (
      <Layout eyebrow={<Eyebrow>{W.home}</Eyebrow>} title={excTitle('photo-skipped')} sub="That's fine. Use what you can easily reach; no climbing or complete inventory is needed."
              actions={<>{done}<Quiet>{contact}</Quiet></>}>
        <section className="hc-card rs-stack-8"><span className="hc-heading rs-title-sm">One fact helps: {W.unitsQuestion.toLowerCase()}</span>
          <span className="hc-caption">It sets which visit price applies. Your count is recorded as reported, not verified.</span>
          <div className="rs-seg" role="radiogroup" aria-label={W.unitsQuestion}>
            {([[1, W.oneUnit], [2, W.twoUnits], [3, W.threeUnits], ['four_plus', W.fourPlus], ['unknown', W.unknownUnits]] as const).map(([v, label]) => (
              <button key={String(v)} type="button" role="radio" aria-checked={h?.units.reported === v} className="rs-seg__btn rs-press" onClick={() => s.act({ type: 'report_units', value: v })}>{label}</button>))}
          </div>
        </section>
      </Layout>
    );
    case 'not-served': return <NotServed />;
    case 'intake-restricted': return (
      <Layout eyebrow={<Eyebrow>{W.membership}</Eyebrow>} title="New joining is paused" sub="BOB has paused new memberships while it protects care already promised to members. Nothing is purchased or charged."
              actions={<>{done}<Quiet>{contact}</Quiet></>}>
        <section className="hc-card"><KV rows={[['Why', <T>[admission limit reason]</T>], ['When it reopens', <T>[only if actually decided]</T>], [W.charged, 'Nothing']]} /></section>
      </Layout>
    );
    case 'resume': return <Resume />;
    case 'terms-changed': {
      const q = s.records?.purchase?.quote;
      const prev = h?.quoteHistory.filter((x) => x.ref === q?.ref).at(-1);
      return (
        <Layout eyebrow={<><Eyebrow>{q ? `${q.ref} ${q.version}` : W.quote}</Eyebrow><Chip>{W.testPrices}</Chip></>} title={excTitle('terms-changed')} note="Acceptance applies only to the current version, reviewed now."
                actions={<><Button kind="primary" onClick={() => { s.acceptVersion(); s.closeExc(); if (!s.page) s.setView(4); }}>Review the current version</Button><Quiet>{contact}</Quiet></>}>
          {q && prev && (
            <section className="hc-card rs-stack-8">
              <span><Tone tone="warning">Review before accepting</Tone></span>
              <KV rows={[['Earlier', `${prev.version} · ${prev.terms} · ${prev.lines.map((l) => `${l.label} ${money(l.amount)}`).join(', ')}`],
                         ['Now', `${q.version} · ${q.terms} · ${q.lines.map((l) => `${l.label} ${money(l.amount)}`).join(', ')}`],
                         ['Why', prev.terms !== q.terms ? <T>[material terms change reason]</T> : `Reported units changed to ${unitsWord(h?.units.reported).toLowerCase()}`],
                         ['Earlier version', 'Can no longer be accepted']]} />
            </section>)}
        </Layout>
      );
    }
    case 'pay-processing': return (
      <Layout eyebrow={payEyebrow} title={excTitle('pay-processing')} sub="Don't pay again. BOB checks this attempt first." actions={<Quiet>{returnLater}{contact}</Quiet>}>
        {last && <ReceiptCard payment={last} />}
        <Button kind="primary" disabled>Pay control closed for this attempt</Button>
      </Layout>
    );
    case 'pay-failed': return (
      <Layout eyebrow={payEyebrow} title={excTitle('pay-failed')} note="Offered only because this attempt definitively failed, so a new attempt is safe."
              actions={<><Button kind="primary" onClick={() => { s.closeExc(); if (!s.page) s.setView(5); }}>Try again</Button><Quiet>{!member && <Link onClick={s.notNow}>{W.notNow}</Link>}{contact}</Quiet></>}>
        {last && <ReceiptCard payment={last} />}
        <section className="hc-card"><KV rows={[['Known failure', <T>[failure reason from processor]</T>], [W.charged, 'Nothing, for this attempt']]} />
          <span className="hc-caption">Everything else is kept: your quote, chosen items and information choices.</span></section>
      </Layout>
    );
    case 'pay-unknown': {
      const rc = h?.cases.find((c) => c.kind === 'payment_reconciliation' && c.state !== 'closed');
      return (
        <Layout eyebrow={payEyebrow} title={excTitle('pay-unknown')} sub="Don't pay again. We're checking this attempt's outcome against the original record." note="A timeout alone is not evidence that no charge occurred."
                actions={<><Button onClick={() => s.contact()}>{W.contactBob}</Button><Quiet>{returnLater}</Quiet></>}>
          {last && <ReceiptCard payment={last} />}
          <section className="hc-card"><KV rows={[['Pay again', 'Not offered until the outcome is known']]} /></section>
          {rc && <CaseCard k={rc} />}
        </Layout>
      );
    }
    case 'activation-pending': return (
      <Layout eyebrow={<Eyebrow>{W.membershipLabel} {h?.membership.id}</Eyebrow>} title={excTitle('activation-pending')} sub="Your payment is confirmed. Membership activation is a separate record and is still pending."
              actions={<>{done}<Quiet>{contact}</Quiet></>}>
        <section className="hc-card"><KV rows={[['Activation follows', <T>[activation rule]</T>], ['Your paid funds', <T>[status of paid funds]</T>], ['Member prices', 'Available once active'],
                                                ['Purchased visit', h?.orders.length ? `${h.orders[0].id} · kept` : 'None']]} /></section>
      </Layout>
    );
    case 'activation-problem': return (
      <Layout eyebrow={<Eyebrow>{W.membershipLabel} {h?.membership.id}</Eyebrow>} title={excTitle('activation-problem')} sub="BOB owns fixing this. No automatic refund amount is approved."
              actions={<>{done}<Quiet>{contact}</Quiet></>}>
        <section className="hc-card"><KV rows={[['What failed', <T>[activation failure]</T>], ['Your paid funds', <T>[status of paid funds]</T>], ['Remedy and timing', <T>[per settled terms]</T>], [W.owner, <T>[accepted owner]</T>]]} /></section>
      </Layout>
    );
    case 'urgent': return (
      <Layout eyebrow={<Eyebrow>{excTitle('urgent')}</Eyebrow>} title="Get qualified help first" sub="Roundstead cannot diagnose or judge safety. Club membership is not an emergency or monitoring service."
              actions={<><Button kind="primary" onClick={() => s.openRoute('Qualified route', '[technician-approved escalation route]')}>Call</Button><Quiet><Link onClick={s.closeExc}>{W.back}</Link></Quiet></>}>
        <section className="hc-card"><KV rows={[['Technician-approved route', <T>[escalation route]</T>], ['Outage route', <T>[outage route and hours]</T>]]} /></section>
      </Layout>
    );
    case 'unavailable': return (
      <Layout eyebrow={<Eyebrow>Service notice</Eyebrow>} title="Roundstead is unavailable right now" sub={<T>[actual limitation]</T>}
              actions={<Quiet><Link onClick={() => s.openRoute('Published fallback', '[fallback contact and hours]')}>Published fallback</Link></Quiet>}>
        <section className="hc-card"><KV rows={[['Your progress', 'Kept; nothing restarts'], [W.paymentTitle, 'Do not pay again; BOB reconciles any attempt']]} /></section>
      </Layout>
    );
    case 'bad-upload': return (
      <Layout eyebrow={<Eyebrow>{W.photos}</Eyebrow>} title={excTitle('bad-upload')} sub="Text inside a file never changes what BOB does. Nothing from it was used, shown or shared."
              actions={<>{done}<Quiet>{contact}</Quiet></>}>
        <section className="hc-card"><KV rows={[['Detected', <T>[a file that does not match this home, or instructions inside a file]</T>], ['Used, shown or shared', 'Nothing from it'], ['Review', 'BOB reviews; you can remove the file']]} /></section>
      </Layout>
    );
  }
}

function NotServed() {
  const s = useStore();
  const [tell, setTell] = useState(false);
  return (
    <Layout eyebrow={<Eyebrow>{W.home}</Eyebrow>} title={excTitle('not-served')} sub={<T>Roundstead does not serve [service area rule] right now. We can't offer a workaround.</T>}
            actions={<><Button onClick={() => s.contact()}>{W.contactBob}</Button><Quiet><Link onClick={() => s.openExc('decline')}>{W.notNow}</Link></Quiet></>}>
      <section className="hc-card"><KV rows={[[W.charged, 'Nothing'], [W.photos, <T>[retention per your choice]</T>], ['Who decides', <T>BOB service lead, [eligibility and capacity rule]</T>]]} /></section>
      <section className="hc-card">
        <label className="hc-toggle rs-toggle">
          <span className="rs-stack-4"><span>Tell me if this changes</span><span className="hc-caption"><T>One message, for that only · [defined use]</T></span></span>
          <input type="checkbox" className="hc-switch" checked={tell} onChange={(e) => { setTell(e.target.checked); s.log('tell_if_changes', { on: e.target.checked }); }} />
        </label>
      </section>
    </Layout>
  );
}

/** A session begun on another device: the record resumes after a check, never another home's context. */
function Resume() {
  const s = useStore();
  const h = s.h;
  const pr = s.records?.progress;
  const resume = () => {
    s.closeExc();
    if (pr?.page) s.openPage(pr.page as never);
    else if (h?.membership.state === 'active') s.setView(8);
    else s.setView((pr?.view ?? (s.records?.purchase ? 4 : 2)) as 1);
  };
  return (
    <Layout eyebrow={<Eyebrow>Welcome back</Eyebrow>} title={excTitle('resume')}
            actions={<><Button kind="primary" onClick={resume}>Continue</Button><Quiet><Link onClick={() => s.openExc('decline')}>{W.notNow}</Link><Link onClick={() => s.contact()}>{W.contactBob}</Link></Quiet></>}>
      <section className="hc-card rs-stack-8">
        <KV rows={[['Saved', h?.membership.state === 'active' ? 'Membership and purchases' : s.records?.purchase ? 'A quote is waiting' : 'Your reported details']]} />
        <span className="hc-caption">Your home, quote and purchases appear after the check. An earlier consent is not reused.</span>
      </section>
      <section className="hc-card rs-stack-8">
        <span className="hc-heading rs-title-sm">Confirm it's you</span>
        <span className="hc-caption"><T>Only your own home's records are shown after this check. [identity check method]</T></span>
        <label className="hc-field"><span><Bracket>[identity check field]</Bracket></span><input className="hc-input" autoComplete="off" aria-label="[identity check field]" /></label>
      </section>
    </Layout>
  );
}
